import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import Layout from './components/common/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import TradingPage from './pages/TradingPage';
import PositionsPage from './pages/PositionsPage';
import EquityOrdersPage from './pages/EquityOrdersPage';
import MasterOrdersPage from './pages/MasterOrdersPage';
import EquityEnginePage from './pages/EquityEnginePage';
import ZerodhaPortfolioPage from './pages/ZerodhaPortfolioPage';
import HistoryPage from './pages/HistoryPage';
import SignalsPage from './pages/SignalsPage';
import BacktestPage from './pages/BacktestPage';
import TestingModulePage from './pages/TestingModulePage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import ComprehensiveActivityPage from './pages/ComprehensiveActivityPage';
import SubscriptionDashboard from './pages/SubscriptionDashboard';
import AdminSubscriptions from './pages/AdminSubscriptions';
import AgentCenterPage from './pages/AgentCenterPage';
import NotificationsPage from './pages/NotificationsPage';
import LandingPage from './pages/LandingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import AdminSubscribersPage from './pages/AdminSubscribersPage';
import UserSubscriberPortal from './pages/UserSubscriberPortal';
import useStore from './store/useStore';
import { auth } from './services/api';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#2563eb', light: '#60a5fa', dark: '#1d4ed8', contrastText: '#ffffff' },
    secondary: { main: '#4f46e5', light: '#818cf8', dark: '#3730a3' },
    success: { main: '#10b981', light: '#34d399', dark: '#059669', contrastText: '#ffffff' },
    warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706', contrastText: '#ffffff' },
    error: { main: '#ef4444', light: '#f87171', dark: '#dc2626' },
    info: { main: '#0284c7', light: '#38bdf8', dark: '#0369a1' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#64748b' },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h4: { fontWeight: 800, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 700, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.04), 0 1px 2px -1px rgba(15, 23, 42, 0.04)',
          border: '1px solid #e2e8f0',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.04), 0 1px 2px -1px rgba(15, 23, 42, 0.04)',
          border: '1px solid #e2e8f0',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '8px 18px',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)' },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        },
        containedSuccess: {
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#f8fafc',
          color: '#475569',
          fontWeight: 700,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          borderBottom: '1px solid #e2e8f0',
        },
        body: {
          fontSize: '0.875rem',
          color: '#1e293b',
          borderBottom: '1px solid #f1f5f9',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.1)',
        },
      },
    },
  },
});

function PrivateRoute({ children }) {
  const token = useStore(s => s.token);
  return token ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const token = useStore(s => s.token);
  const user = useStore(s => s.user);
  if (!token) return <Navigate to="/login" replace />;
  if (user && user.role !== 'admin') return <Navigate to="/my-subscription" replace />;
  return children;
}

export default function App() {
  const { token, setUser } = useStore();

  useEffect(() => {
    if (token) {
      auth.me().then(res => setUser(res.data.user)).catch(() => {});
    }
  }, [token]);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Toaster position="top-right" toastOptions={{ duration: 4000, style: { borderRadius: 8, fontWeight: 500 } }} />
        <BrowserRouter>
          <Routes>
            {/* Public Marketing & Legal Pages */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/login" element={<LoginPage />} />

            {/* Subscriber Portal for Regular Users */}
            <Route path="/my-subscription" element={<PrivateRoute><UserSubscriberPortal /></PrivateRoute>} />

            {/* Superadmin Subscribers & Coupon Management */}
            <Route path="/admin/subscribers" element={<AdminRoute><AdminSubscribersPage /></AdminRoute>} />

            {/* Authenticated Internal Trading Platform - Superadmin ONLY */}
            <Route element={<AdminRoute><Layout /></AdminRoute>}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/trading" element={<TradingPage />} />
              <Route path="/orders" element={<MasterOrdersPage />} />
              <Route path="/positions" element={<MasterOrdersPage />} />
              <Route path="/equity-orders" element={<MasterOrdersPage />} />
              <Route path="/history" element={<MasterOrdersPage />} />
              <Route path="/signals" element={<SignalsPage />} />
              <Route path="/backtest" element={<BacktestPage />} />
              <Route path="/testing" element={<TestingModulePage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/equity-engine" element={<EquityEnginePage />} />
              <Route path="/portfolio" element={<ZerodhaPortfolioPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/activity-logs" element={<ComprehensiveActivityPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/subscriptions" element={<SubscriptionDashboard />} />
              <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
              <Route path="/admin/agent-center" element={<AgentCenterPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
